'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import classNames from 'classnames';

import UserStore from '@stores/UserStore';
import AuthStore from '@stores/AuthStore';
import Analytics from '@utils/Analytics';
import validator from 'validator';
import Select from '@pro/components/Widgets/Select.react';
import Alert from '@components/Widgets/Alert/Alert.react';
import EditImage from '@components/Recipes/Editor/EditImage.react';
import { getConfig } from '@utils/Env';

import './ContactFormModal.scss';

export default class ContactFormModal extends Component {
    static propTypes = {
        body: PropTypes.string,
        defaultSubject: PropTypes.string, // For setting default subject when no defaultSubject prop
        sourceLabel: PropTypes.string, // Customer-friendly label for where contact originated
        children: PropTypes.node,
        className: PropTypes.string,
        // External modal state management
        isOpen: PropTypes.bool,
        onRequestClose: PropTypes.func,
    };

    static defaultProps = {
        defaultSubject: 'general',
        body: '',
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            isOpen: false,
            name: '',
            email: '',
            subject: props.defaultSubject || 'general',
            message: props.body,
            screenshots: ['', '', ''], // Up to 3 screenshot URLs
            submitting: false,
            submitted: false,
            error: null,
        };
    }

    componentDidMount = () => {
        const user = UserStore.getUser();

        if (user) {
            this.setState({
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                email: user.email || '',
            });
        }
    }

    openModal = () => {
        if (this.props.isOpen !== undefined) {
            // External state management - do nothing, parent handles it
            return;
        }
        this.setState({ 
            isOpen: true, 
            submitted: false, 
            error: null,
            subject: this.props.defaultSubject || 'general',
            message: this.props.body || ''
        });

        // Fire analytics event for modal opening
        Analytics.openContactForm({
            'Contact Source': this.props.sourceLabel || 'Unknown',
            'User Type': UserStore.getUser() ? 'Authenticated' : 'Anonymous',
        });
    }

    closeModal = () => {
        // Fire analytics event for modal closing
        Analytics.closeContactForm({
            'Contact Source': this.props.sourceLabel || 'Unknown',
            'User Type': UserStore.getUser() ? 'Authenticated' : 'Anonymous',
        });

        if (this.props.onRequestClose) {
            this.props.onRequestClose();
        } else {
            this.setState({ isOpen: false, submitted: false, submitted: false, error: null });
        }
    }

    isExternalState = () => {
        return this.props.isOpen !== undefined;
    }

    getModalOpen = () => {
        return this.isExternalState() ? this.props.isOpen : this.state.isOpen;
    }

    handleInputChange = (field) => (ev) => {
        this.setState({ [field]: ev.target.value });
    }

    handleSubjectChange = (value) => {
        this.setState({ subject: value });
    }

    handleScreenshotChange = (index) => (url) => {
        const { screenshots } = this.state;
        const newScreenshots = [...screenshots];
        newScreenshots[index] = url;
        this.setState({ screenshots: newScreenshots });
    }


    handleSubmit = (ev) => {
        ev.preventDefault();

        const { name, email, subject, message, screenshots } = this.state;
        const user = UserStore.getUser();

        // Validation - only require name/email if user is not logged in
        if (!user && (!name.trim() || !email.trim())) {
            this.setState({ error: 'Please fill in your name and email address.' });
            return;
        }

        if (!message.trim()) {
            this.setState({ error: 'Please enter a message.' });
            return;
        }

        if (!user && email && !validator.isEmail(email)) {
            this.setState({ error: 'Please enter a valid email address.' });
            return;
        }

        this.setState({ submitting: true, error: null });

        // Submit contact form data directly to the API with proper error handling
        const screenshotUrls = screenshots.filter(url => url.trim() !== '');
        
        const payload = {
            distinct_id: user ? user.uuid : null,
            session_id: getConfig("instance_id"),
            event: 'Submit Contact Form',
            current_url: document.location.href,
            properties: {
                'Name': user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : name,
                'Email': user ? user.email : email,
                'Subject': subject,
                'Message': message,
                'Message Length': message.length,
                'User Type': user ? 'Authenticated' : 'Anonymous',
                'Screenshots': screenshotUrls,
                'Contact Source': this.props.sourceLabel || 'Unknown',
            }
        };

        // Submit directly via AuthStore.fetch with proper error handling
        AuthStore.fetch(
            getConfig('users_api') + '/track',
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; schema=track/event/1' },
                body: JSON.stringify(payload),
            },
            true // fire and forget flag
        ).then(
            (response) => {
                // Check if we're online and the request actually succeeded
                if (!navigator.onLine) {
                    throw new Error('No network connection');
                }
                
                // Also track via Analytics for consistent tracking
                Analytics.submitContactForm({
                    'Name': user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : name,
                    'Email': user ? user.email : email,
                    'Subject': subject,
                    'Message': message,
                    'Message Length': message.length,
                    'User Type': user ? 'Authenticated' : 'Anonymous',
                    'Screenshots': screenshotUrls,
                    'Contact Source': this.props.sourceLabel || 'Unknown',
                });

                this.setState({
                    submitting: false,
                    submitted: true,
                });

                // Auto-close after 2 seconds
                setTimeout(() => {
                    this.closeModal();
                }, 2000);
            },
            (error) => {
                this.setState({
                    submitting: false,
                    error: 'There was an error sending your message. Please try again.'
                });
            }
        ).catch((error) => {
            // Additional catch for any other errors (like network failures)
            this.setState({
                submitting: false,
                error: 'There was an error sending your message. Please try again.'
            });
        });
    }

    render = () => {
        const { children, className } = this.props;
        const { name, email, subject, message, screenshots, submitting, submitted, error } = this.state;
        const isOpen = this.getModalOpen();
        const user = UserStore.getUser();

        // Subject options matching existing mailto subjects
        const subjectOptions = [
            { value: 'general', label: 'General Question' },
            { value: 'technical', label: 'Technical Support' },
            { value: 'billing', label: 'Billing Question' },
            { value: 'sales', label: 'Sales Inquiry' },
            { value: 'feature', label: 'Feature Request' },
            { value: 'rd-consult', label: 'Request Dietitian Consult' },
            { value: 'report-issue', label: 'Report an Issue' },
        ];

        return (
            <>
                {/* Only render trigger if not using external state management */}
                {!this.isExternalState() && (
                    <span onClick={this.openModal} className={classNames('contact-form-btn', className)}>
                        {children || 'Contact Support'}
                    </span>
                )}

                {isOpen && (
                    <Modal
                        isOpen={true}
                        onRequestClose={this.closeModal}
                        contentLabel="Contact Support"
                        className="el-modal el-modal3 el-modal3-centered"
                        overlayClassName="el-modal-overlay"
                        closeTimeoutMS={250}>

                        <section className="el-modal-container el-modal3-container contact-form-modal-container">
                            <header>
                                <h2>Contact Support</h2>
                                <button className="el-modal-close-x" onClick={this.closeModal}>
                                    <i className="icon-close-x" />
                                    <span className="assistive-text">Close Modal</span>
                                </button>
                            </header>

                            <div className="el-modal-body-container el-modal3-body-container el-fonts">
                                {submitted ? (
                                    <div className="contact-form-success">
                                        <i className="icon-checkmark-circle" />
                                        <p>Thank you! Your message has been sent. We'll get back to you soon.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={this.handleSubmit} className="contact-form">
                                        {!user && (
                                            <>
                                                <div className="el-labeled-input">
                                                    <label htmlFor="contact-name">Name *</label>
                                                    <input
                                                        id="contact-name"
                                                        type="text"
                                                        value={name}
                                                        onChange={this.handleInputChange('name')}
                                                        placeholder="Your name"
                                                        disabled={submitting}
                                                        required
                                                    />
                                                </div>

                                                <div className="el-labeled-input">
                                                    <label htmlFor="contact-email">Email *</label>
                                                    <input
                                                        id="contact-email"
                                                        type="email"
                                                        value={email}
                                                        onChange={this.handleInputChange('email')}
                                                        placeholder="your.email@example.com"
                                                        disabled={submitting}
                                                        required
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {user && (
                                            <div className="user-info">
                                                <p><strong>Sending as:</strong> {user.first_name} {user.last_name} ({user.email})</p>
                                            </div>
                                        )}

                                        <div className="el-labeled-input">
                                            <label htmlFor="contact-subject">Subject</label>
                                            <Select
                                                options={subjectOptions}
                                                value={subject}
                                                onChange={this.handleSubjectChange}
                                                disabled={submitting}
                                                defaultClassName="el-select-container"
                                                className="contact-subject-select"
                                            />
                                        </div>

                                        <div className="el-labeled-input">
                                            <label htmlFor="contact-message">Message *</label>
                                            <textarea
                                                id="contact-message"
                                                value={message}
                                                onChange={this.handleInputChange('message')}
                                                placeholder="Please describe your question or issue..."
                                                rows={4}
                                                disabled={submitting}
                                                required
                                            />
                                        </div>

                                        {user && ['technical', 'feature', 'report-issue'].includes(subject) && (
                                            <div className="screenshots-section">
                                                <label>Screenshots (optional)</label>
                                                <p className="screenshot-help">Include up to 3 screenshots to help us understand your issue</p>

                                                <div className="screenshot-uploads">
                                                    {screenshots.map((screenshot, index) => (
                                                        <EditImage
                                                            key={index}
                                                            className="feather feather-camera"
                                                            image={screenshot}
                                                            onChangeImage={this.handleScreenshotChange(index)}
                                                            showFooter={true}
                                                            preload={false}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {error && (
                                            <Alert type="warning">{error}</Alert>
                                        )}
                                    </form>
                                )}
                            </div>

                            {!submitted && (
                                <footer>
                                    <button
                                        onClick={this.closeModal}
                                        className="el-modal-cancel-btn"
                                        disabled={submitting}>
                                        Cancel
                                    </button>
                                    <button
                                        onClick={this.handleSubmit}
                                        className="el-modal-ok-btn"
                                        disabled={submitting || !message.trim() || (!user && (!name.trim() || !email.trim()))}>
                                        {submitting ? 'Sending...' : 'Send Message'}
                                    </button>
                                </footer>
                            )}
                        </section>
                    </Modal>
                )}
            </>
        );
    }

}
